import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/workplace-xg/workplace-xg/node_modules/gatsby-theme-carbon/src/templates/Default.js";
import Divider from "../../components/Divider";
import TwoSections from "../../components/TwoSections";
import TwoSectionsImages from "../../components/TwoSectionsImages";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const PageDescription = makeShortcode("PageDescription");
const Video = makeShortcode("Video");
const Row = makeShortcode("Row");
const Column = makeShortcode("Column");
const ResourceCard = makeShortcode("ResourceCard");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <PageDescription mdxType="PageDescription">
      <p>{`Unity, not uniformity. “A corporation should be like a painting; everything visible should contribute to the correct total statement.” —Eliot Noyes`}</p>
    </PageDescription>
    <PageDescription mdxType="PageDescription">
      <p>{`IBM sites convey an experience that’s uniquely IBM—warm and welcoming, inclusive and diverse, intuitive and familiar.`}</p>
    </PageDescription>
    <PageDescription mdxType="PageDescription">
      <p>{`This resource provides design principles, planning concepts and space types that inform all workplace projects across IBM’s real estate portfolio. We endeavor to create a workplace experience for employees, clients and guests that facilitates productivity and collaboration, inspires curiosity and creativity, and embodies IBM’s brand and culture.`}</p>
    </PageDescription>
    <h2>{`IBM Design Language`}</h2>
    <p>{`IBM Design Language distinguishes every experience we make. It delivers clear criteria for the conception, creativity and craftsmanship the IBM brand deserves. Through purposeful composition of our environments, architecture, identity and space planning, every IBM facility is a powerful opportunity to engage people with our brand and culture.`}</p>
    <p>{`While focused on the workplace design discipline, these guidelines are derived from the broader IBM Design Language. Refer to it for our core principles, design philosophy and brand elements, such as typography, color palette, identity and more. Certain elements of our toolkit are designed to be flexible to suit the needs of a project’s requirements and location or the end user.`}</p>
    <p>{`The IBM Design Language doesn’t aim to limit or constrict workplace design, but to enhance it in every way. By providing a common language and lexicon, we can unify our facilities around the world while providing unique opportunities for localization to celebrate our diversity. Learn more about `}<a parentName="p" {...{
        "href": "https://www.ibm.com/design/language/"
      }}>{`IBM Design Language`}</a>{`.`}</p>
    <Video title="" vimeoId="296064420" mdxType="Video" />
    <Row className="resource-card-group" mdxType="Row">
      <Column colMd={4} colLg={4} noGutterSm mdxType="Column">
  <ResourceCard subTitle="IBM Design Language" aspectRatio="2:1" actionIcon="launch" href="https://www.ibm.com/design/language/" mdxType="ResourceCard">
          <img {...{
            "src": "/workplace-xg/be3a39052a0eec8bce3dd41ca71c2881/eye-bee-m.svg",
            "alt": "eye-bee-m icon"
          }}></img>
  </ResourceCard>
      </Column>
    </Row>
    <h2>{`IBM Workplace design`}</h2>
    <p>{`The design of our workplace environments is interdisciplinary, bringing together many facets of design, including interior architecture, space planning, materials and finishes, brand expression, and signage. While each of these dimensions requires a unique skill set, all of them can be unified through a common philosophy. Each section of these guidelines is designed to provide creative direction for various aspects of a project.`}</p>
    <Video title="" vimeoId="527395979" mdxType="Video" />
    <Divider mdxType="Divider" />
    <TwoSections title="Design characteristics" mdxType="TwoSections">
  <Column mdxType="Column">
        <p>{`IBM has a rich legacy of design across many disciplines—from industrial design to architecture. Design characteristics are the elements of a space that make our environments instantly recognizable as IBM. These characteristics are derived from our design language and serve to reinforce our brand within the built environment. We invite our design partners to explore how our design character can be reflected regionally around the world. Learn more about `}<a parentName="p" {...{
            "href": "/workplace-xg/design-characteristics"
          }}>{`design characteristics`}</a>{`.`}</p>
  </Column>
    </TwoSections>
    <Divider mdxType="Divider" />
    <TwoSections title="Interior architecture" mdxType="TwoSections">
  <Column mdxType="Column">
    
  The interior architecture and design of IBM environments must deliver a cohesive experience. Our design philosophy and characteristics can inform creative decision-making across all dimensions of the design process—from furniture selection to finish palette. This guidance provides a framework so that our brand character isn’t just seen—but also felt in all our environments. Collectively, both overt and subtle design choices create the connective tissue that supports our culture and community.
  </Column>
    </TwoSections>
    <Divider mdxType="Divider" />
    <TwoSections title="Planning and space types" mdxType="TwoSections">
  <Column mdxType="Column">
    
    <p>We build activity-based workplaces that are the platform for the next century of innovative ideas. Every IBM employee, client and guest should feel welcomed, guided and included from the moment they arrive.</p>
    <p>All IBM work environments are comprised of a series of defined space types. These zones have been created to provide a consistent workplace experience for any IBM employee or client around the world.</p>
    <p>Additionally, the design of these zones is infused with the IBM Design Language and philosophy. This framework applies to not only brand identity and signage, but also underlies a cohesive approach to interior design, fixtures, furniture, finishes and architecture.</p>
  </Column>
    </TwoSections>
    <Divider mdxType="Divider" />
    <TwoSections title="Brand expression" mdxType="TwoSections">
      <Column mdxType="Column">
        <p>{`The IBM brand can manifest in the built environment through exciting and provocative applications. Opportunities for brand expression can be overt, such as signage and environmental graphics, or subtle, such as choices in material and pattern. Collectively, all these elements create a differentiated experience for our environments.`}</p>
        <p>{`It’s critical that we brand our environments with timeless symbols of our identity and culture. Unlike marketing campaigns, these motifs are enduring elements that will stay relevant as our products and services evolve. Avoid using advertising or other campaign media that can make our spaces feel outdated over time. Learn more about `}<a parentName="p" {...{
            "href": "/workplace-xg/brand-expression/overview"
          }}>{`brand expression`}</a>{`.`}</p>
      </Column>
    </TwoSections>
    <h2>{`Roles and responsibilities`}</h2>
    <Divider mdxType="Divider" />
    <TwoSections title="IBM Global Real Estate" mdxType="TwoSections">
  <Column mdxType="Column">
    
    <p>IBM Global Real Estate manages our global real estate portfolio, which is made up of a mix of office, data center, manufacturing, development and lab space. We manage a range of real estate disciplines, including strategy, space management, workplace design, engineering and construction, leasing, real estate acquisitions and dispositions, business acquisition, integration and divestitures, facility operations, and environmental and energy management.</p>
    <p>This site is a resource for IBM Global Real Estate leaders responsible for design delivery and execution of projects worldwide. This group includes teams involved in management of the project requirements, design consultants and those with oversight of the design process.</p>
  </Column>
    </TwoSections>
    <Divider mdxType="Divider" />
    <TwoSections title="IBM Brand Experience and Design" mdxType="TwoSections">
  <Column mdxType="Column">
    
    <p>The IBM Brand team is responsible for ensuring appropriate execution and expression of our core design philosophy and principles across every expression of the IBM brand, including digital, industrial, event and workplace design.</p>
    <p>Collaboration with our design partners and real estate colleagues is critical for thoughtful integration of brand identity within the physical environment. This collaboration requires engagement early in the design process to ensure coordination across interior design and environmental elements.</p>
  </Column>
    </TwoSections>
    <Divider mdxType="Divider" />
    <TwoSections title="Agency partners" mdxType="TwoSections">
  <Column mdxType="Column">
    
    IBM seeks innovative thinking from top talent across the industry. This resource is an active platform, showcasing marquee examples of design excellence. Our design philosophy and principles inspire and facilitate creativity. We look to our agency partners to deliver innovation within the context of our projects. This iterative, co-creative approach challenges all parties to bring their expertise to bear for the achievement and reward of dynamic, high-performance environments.
  </Column>
    </TwoSections>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      